/**
 * TanStack Query defaults (plan D2).
 *
 * The one rule that matters: **retry what the server said is retryable, and
 * nothing else.** `ApiError.retryable` is set per code in `@rd/shared`'s
 * catalog, so the client does not re-derive it from a status. Retrying a 400 is
 * noise; retrying a 403 is noise the user watches; retrying a job submission is
 * a second SIM activation.
 */

import { QueryClient } from '@tanstack/react-query';

import { ApiClientError, NetworkError } from './apiClient.js';

export const LIST_STALE_MS = 30_000;

export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= 2) return false;
  if (error instanceof NetworkError) return true;
  if (error instanceof ApiClientError) return error.retryable;
  return false;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: shouldRetry,
        staleTime: LIST_STALE_MS,
        // A job poll owns its own cadence through `refetchInterval`; refetching
        // on focus as well would double the request rate every time someone
        // alt-tabs back, which on a 5 s interval is visible in the API logs.
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
      },
      mutations: {
        // A write is never retried automatically. The user pressed a button
        // once; the panel does not get to press it twice.
        retry: false,
      },
    },
  });
}
